import { useEffect } from "react";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import { supabase } from "@/integrations/supabase/client";
import { REDIRECT_URI } from "@/lib/googleAuth";

/**
 * GoogleOAuthCallback
 *
 * This page lives at /oauth/google/callback.
 * Google redirects here after the user grants (or denies) permission.
 *
 * Flow:
 *  1. Reads ?code= from the URL
 *  2. Sends it to the exchange-google-token Edge Function
 *  3. Posts a message to the parent (opener) window
 *  4. Closes itself
 */
const GoogleOAuthCallback = () => {
  useEffect(() => {
    const run = async () => {
      const params = new URLSearchParams(window.location.search);
      const code = params.get("code");
      const error = params.get("error");

      // User denied access
      if (error || !code) {
        window.opener?.postMessage(
          { type: "google-auth-error", message: error || "No authorization code received" },
          window.location.origin
        );
        window.close();
        return;
      }

      try {
        // Get the current session so we can pass the JWT to the Edge Function
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) throw new Error("No active session — please log in first.");

        const { data, error: fnError } = await supabase.functions.invoke("exchange-google-token", {
          body: { code, redirect_uri: REDIRECT_URI },
          headers: { Authorization: `Bearer ${session.access_token}` },
        });

        if (fnError || !data?.success) {
          throw new Error(fnError?.message || "Token exchange failed");
        }

        // Signal success to the parent window and close popup
        window.opener?.postMessage("google-auth-success", window.location.origin);
      } catch (err: any) {
        window.opener?.postMessage(
          { type: "google-auth-error", message: err.message },
          window.location.origin
        );
      } finally {
        window.close();
      }
    };

    run();
  }, []);

  return <LoadingSpinner size="fullscreen" label="Connecting your Google account…" className="min-h-screen bg-[#f9fafb]" />;
};

export default GoogleOAuthCallback;
